/**
 * web/touchControls.ts
 * DOM-based D-pad + action buttons for the Vite bundle.
 *
 * Mirrors the touch surface from `src/game/input/{DPad,ActionButtons}.tsx`
 * without React or gesture-handler: plain buttons that flip `inputState`
 * flags on pointerdown/up. The D-pad has Up/Down/Left/Right + an optional
 * Up control surfaced for the space scene; the action panel has Jump and
 * Attack buttons. Each button is at least 64×64 px to honour the AGENTS.md
 * 60×60 pt accessibility floor.
 *
 * @module web/touchControls
 */

/// <reference lib="dom" />

import { inputState, clearAllInputState } from '@src/game/input/inputState';

/** Handle returned by {@link mountTouchControls}. */
export interface TouchControlsHandle {
  /** Detach all listeners and remove the controls from the DOM. */
  destroy: () => void;
}

interface MountOptions {
  /** When true, the D-pad includes a dedicated Up button (space scene). */
  showUp?: boolean;
}

type Action = 'left' | 'right' | 'down' | 'up' | 'jump' | 'attack';

const setFlag = (action: Action, value: boolean): void => {
  switch (action) {
    case 'left': inputState.left = value; break;
    case 'right': inputState.right = value; break;
    case 'down': inputState.down = value; break;
    case 'up': inputState.up = value; break;
    case 'jump':
      if (value && !inputState.jumpHeld) inputState.jumpPressed = true;
      inputState.jumpHeld = value;
      break;
    case 'attack':
      if (value && !inputState.attackHeld) inputState.attackPressed = true;
      inputState.attackHeld = value;
      break;
  }
};

const wireButton = (
  btn: HTMLButtonElement,
  action: Action,
  trackers: (() => void)[],
): void => {
  const down = (e: Event): void => {
    e.preventDefault();
    setFlag(action, true);
  };
  const up = (e: Event): void => {
    e.preventDefault();
    setFlag(action, false);
  };
  btn.addEventListener('pointerdown', down);
  btn.addEventListener('pointerup', up);
  btn.addEventListener('pointercancel', up);
  btn.addEventListener('pointerleave', up);
  trackers.push(() => {
    btn.removeEventListener('pointerdown', down);
    btn.removeEventListener('pointerup', up);
    btn.removeEventListener('pointercancel', up);
    btn.removeEventListener('pointerleave', up);
  });
};

const makeBtn = (label: string, cls: string): HTMLButtonElement => {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = cls;
  b.textContent = label;
  b.setAttribute('aria-label', label);
  return b;
};

/**
 * Mount the D-pad + action panel as fixed-position children of `host`.
 * The host is expected to be the game-screen root (a single absolutely-
 * positioned full-viewport div).
 */
export function mountTouchControls(host: HTMLElement, opts: MountOptions = {}): TouchControlsHandle {
  const wrap = document.createElement('div');
  wrap.className = 'sf-touch-controls';
  wrap.dataset['testid'] = 'touch-controls';

  const dpad = document.createElement('div');
  dpad.className = 'sf-dpad';
  const leftBtn = makeBtn('←', 'sf-dpad-btn sf-dpad-left');
  const rightBtn = makeBtn('→', 'sf-dpad-btn sf-dpad-right');
  const downBtn = makeBtn('↓', 'sf-dpad-btn sf-dpad-down');
  dpad.append(leftBtn, rightBtn, downBtn);
  let upBtn: HTMLButtonElement | null = null;
  if (opts.showUp) {
    upBtn = makeBtn('↑', 'sf-dpad-btn sf-dpad-up');
    dpad.append(upBtn);
  }

  const actions = document.createElement('div');
  actions.className = 'sf-actions';
  const jumpBtn = makeBtn('JUMP', 'sf-action-btn sf-action-jump');
  const attackBtn = makeBtn('FIRE', 'sf-action-btn sf-action-attack');
  actions.append(attackBtn, jumpBtn);

  wrap.append(dpad, actions);
  host.append(wrap);

  const detachers: (() => void)[] = [];
  wireButton(leftBtn, 'left', detachers);
  wireButton(rightBtn, 'right', detachers);
  wireButton(downBtn, 'down', detachers);
  if (upBtn) wireButton(upBtn, 'up', detachers);
  wireButton(jumpBtn, 'jump', detachers);
  wireButton(attackBtn, 'attack', detachers);

  return {
    destroy: () => {
      for (const fn of detachers) fn();
      wrap.remove();
      // DECISION: Clear held + rising-edge flags so a button still pressed at
      // teardown (or a pointerup we never saw) can't latch into the next screen.
      clearAllInputState();
    },
  };
}
